import {combine, guard, sample} from 'effector';
import i18next from 'i18next';
import _ from 'lodash';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';

import {
    batchNumberModal$,
    blockingForm$,
    blockingFormChanged,
    codes$,
    codesChanged,
    commissioningForm$,
    commissioningFormChanged,
    createBlockingDocument,
    createCommissioningDocument,
    createDisaggregationDocument,
    createDropOutDocument,
    createMovingDocument,
    createUtilisationDocument,
    data$,
    dataChanged,
    disaggregationType$,
    disaggregationTypeChanged,
    DocumentCreatePreviewGate,
    DocumentsCreateGate,
    dropOutForm$,
    dropOutFormChanged,
    fetchGtinInfoFx,
    file$,
    fileChanged,
    gtins$,
    isUtilisationGroup$,
    lastCreatedDocumentId$,
    lastCreatedInvocieId$,
    movingForm$,
    movingFormChanged,
    sendBlockingFx,
    sendCommissioningFx,
    sendDisaggregationFx,
    sendDropOutFx,
    sendMovingFx,
    sendUPDFx,
    sendUtilisationFx,
    setLastCreatedDocumentId,
    setLastCreatedInvocieId,
    showBatchNumberModal,
    confirmBatchNumberModal,
    step$,
    stepChanged,
    UPDString$,
    UPDStringChanged,
    utilisationForm$,
    utilisationFormChanged,
    utilisationGroupChanged,
    waitDocumentInGridAndRedirectFx,
    stopDocumentInGridPolling,
    createDocumentReturnGoodsFx,
} from './index';

sample({
    clock: DocumentsCreateGate.close,
    target: stopDocumentInGridPolling,
});

const onDocumentCreated = (reportId: string) => {
    setLastCreatedDocumentId(reportId);

    waitDocumentInGridAndRedirectFx(reportId);
};

data$
    .on(dataChanged, (state, payload) => {
        if (!payload) return [];
        const newState = _.cloneDeep(state);
        const id = newState.findIndex((s) => s.identityCode === payload.identityCode);
        if (id === -1) return [...newState, payload];
        newState.splice(id, 1);
        return newState;
    })
    .reset([
        DocumentsCreateGate.close,
        dropOutFormChanged,
        commissioningFormChanged,
        movingFormChanged,
        blockingFormChanged,
        disaggregationTypeChanged,
    ]);
dropOutForm$.on(dropOutFormChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
utilisationForm$
    .on(utilisationFormChanged, (state, payload) => ({...state, ...payload}))
    .reset(DocumentsCreateGate.close);
isUtilisationGroup$
    .on(utilisationGroupChanged, (state, payload) => payload)
    .reset(DocumentsCreateGate.close);
commissioningForm$
    .on(commissioningFormChanged, (_, payload) => payload)
    .reset(DocumentsCreateGate.close);
movingForm$.on(movingFormChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
blockingForm$.on(blockingFormChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
disaggregationType$
    .on(disaggregationTypeChanged, (_, payload) => payload)
    .reset(DocumentsCreateGate.close);
step$.on(stepChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
file$
    .on(fileChanged, (_, payload) => payload)
    .reset([DocumentsCreateGate.close, disaggregationTypeChanged]);
codes$
    .on(codesChanged, (_, payload) => payload)
    .reset([DocumentsCreateGate.close, disaggregationTypeChanged]);
gtins$.on(fetchGtinInfoFx.done, (_, payload) => payload.result).reset(DocumentsCreateGate.close);
UPDString$.on(UPDStringChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
lastCreatedDocumentId$.on(setLastCreatedDocumentId, (_, payload) => payload);
lastCreatedInvocieId$.on(setLastCreatedInvocieId, (_, payload) => payload);

batchNumberModal$
    .on(showBatchNumberModal, (state, payload) => payload)
    .reset(DocumentsCreateGate.close);

sendDropOutFx.fail.watch(({error}) => showRestError(error as RestError));

sendDropOutFx.done.watch((data) => {
    const reportId = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(reportId);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: reportId}), {
        className: 'Toastify__toast__wide',
    });
    history.push(ROUTE.DOCUMENTS_WITHDRAWAL);
});

sendUtilisationFx.fail.watch(({error}) => showRestError(error as RestError));

sendUtilisationFx.done.watch((data) => {
    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
});

sendCommissioningFx.fail.watch(({error}) => showRestError(error as RestError));

sendCommissioningFx.done.watch((data) => {
    onDocumentCreated(data.result?.data?.data?.reportId || '');
});

sendUPDFx.fail.watch(({error}) => showRestError(error as RestError));

sendUPDFx.done.watch((data) => {
    onDocumentCreated(data.result?.data?.data?.reportId || '');
});

sendMovingFx.fail.watch(({error}) => showRestError(error as RestError));

sendMovingFx.done.watch((data) => {
    history.push(ROUTE.DOCUMENTS_MOVING);

    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: id}), {
        className: 'Toastify__toast__wide',
    });
});

sendBlockingFx.fail.watch(({error}) => showRestError(error as RestError));

sendBlockingFx.done.watch((data) => {
    const reportId = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(reportId);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: reportId}), {
        className: 'Toastify__toast__wide',
    });
    history.push(ROUTE.DOCUMENTS_BLOCKING);
});

sendDisaggregationFx.fail.watch(({error}) => showRestError(error as RestError));

sendDisaggregationFx.done.watch((data) => {
    onDocumentCreated(data.result?.reportId || '');
});

createDocumentReturnGoodsFx.fail.watch(({error}) => showRestError(error as RestError));

createDocumentReturnGoodsFx.done.watch((data) => {
    const id = data.result?.reportId || '';
    onDocumentCreated(id);
});

sample({
    clock: createDropOutDocument,
    source: combine({
        dropOutForm: dropOutForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({dropOutForm, scannedCodes, uploadCodes}) => ({
        ...dropOutForm,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
        documentId: dropOutForm.documentId ?? '',
    }),
    target: sendDropOutFx,
});

sample({
    clock: createUtilisationDocument,
    source: combine({
        utilisationForm: utilisationForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({
        utilisationForm: {
            factoryId,
            storageLocationId,
            applicationDate,
            manufacturingDate,
            groupMarkingCode,
            batchNumber,
        },
        scannedCodes,
        uploadCodes,
    }) => ({
        factoryId,
        storageLocationId,
        applicationDate,
        manufacturingDate,
        groupMarkingCode,
        batchNumber,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendUtilisationFx,
});

const utilisationCreated = sample({
    clock: sendUtilisationFx.done,
    source: utilisationForm$,
    fn: (form, {result}) => ({
        batchNumberSource: form.batchNumberSource,
        reportId: result?.data?.data?.reportId || '',
    }),
});

sample({
    source: guard({
        source: utilisationCreated,
        filter: ({batchNumberSource}) => batchNumberSource === 'manual',
    }),
    fn: ({reportId}) => reportId,
    target: waitDocumentInGridAndRedirectFx,
});

sample({
    source: guard({
        source: utilisationCreated,
        filter: ({batchNumberSource}) => batchNumberSource === 'auto',
    }),
    fn: () => true,
    target: showBatchNumberModal,
});

sample({
    clock: confirmBatchNumberModal,
    source: lastCreatedDocumentId$,
    fn: (reportId) => reportId || '',
    target: waitDocumentInGridAndRedirectFx,
});

sample({
    clock: createCommissioningDocument,
    source: combine({
        commissioningForm: commissioningForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({commissioningForm, scannedCodes, uploadCodes}) => ({
        ...commissioningForm,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendCommissioningFx,
});

sample({
    clock: createMovingDocument,
    source: combine({
        movingForm: movingForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({
        movingForm: {storageLocationFrom, storageLocationTo, note, employeeFullName},
        scannedCodes,
        uploadCodes,
    }) => ({
        storageLocationFrom,
        storageLocationTo,
        note,
        employeeFullName,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendMovingFx,
});

sample({
    clock: createDisaggregationDocument,
    source: combine({
        disaggregationType: disaggregationType$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({disaggregationType, scannedCodes, uploadCodes}) => ({
        disaggregationType,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendDisaggregationFx,
});

sample({
    clock: createBlockingDocument,
    source: combine({
        blockingForm: blockingForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({blockingForm, scannedCodes, uploadCodes}) => ({
        ...blockingForm,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
        documentId: blockingForm.documentId,
    }),
    target: sendBlockingFx,
});

sample({
    clock: UPDStringChanged,
    target: sendUPDFx,
});

sample({
    clock: DocumentCreatePreviewGate.open,
    source: codes$,
    target: fetchGtinInfoFx,
});
